/**
 * Модалка создания и редактирования заявки на диагностику.
 * Шторка снизу. Поддерживает парсинг данных из 1С.
 * 
 * Пропсы:
 *   order — объект заявки для редактирования (null если создание)
 *   token — токен авторизации
 *   onClose — функция закрытия
 *   onSave — колбэк после успешного сохранения
 * 
 * Применяется: Master
 */

import { useState, useEffect, useRef } from 'react'
import { formatPhone } from '../../shared/lib/phone'
import { orderModalStyles } from './orderModalStyles'

const engineTypes = ['Бензиновый', 'Дизельный', 'Гибридный', 'Электро']

export default function OrderModal({ order, token, onClose, onSave }) {
    const [brands, setBrands] = useState([])
    const [models, setModels] = useState([])
    const [brandSearch, setBrandSearch] = useState(order?.car_brand || '')
    const [modelSearch, setModelSearch] = useState(order?.car_model || '')
    const [showBrands, setShowBrands] = useState(false)
    const [showModels, setShowModels] = useState(false)
    const [showEngineTypes, setShowEngineTypes] = useState(false)
    const [selectedBrandId, setSelectedBrandId] = useState(null)
    const [selectedModelId, setSelectedModelId] = useState(null)
    const [saving, setSaving] = useState(false)
    const savingRef = useRef(false)
    const [errors, setErrors] = useState({})
    const [apiError, setApiError] = useState('')
    const [mechanics, setMechanics] = useState([])
    const [mechanicId, setMechanicId] = useState('')
    const [mechanicsError, setMechanicsError] = useState(false)
    const [parsed, setParsed] = useState(null)
    useEffect(() => {
        const previous = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        const onKey = event => { if (event.key === 'Escape') onClose() }
        window.addEventListener('keydown', onKey)
        return () => { document.body.style.overflow = previous; window.removeEventListener('keydown', onKey) }
    }, [onClose])
    useEffect(() => {
        if (order) return
        const controller = new AbortController()
        fetch('/api/orders/mechanics/list', { signal: controller.signal, headers: { Authorization: `Bearer ${token}` } })
            .then(async response => {
                if (!response.ok) throw new Error('load')
                const data = await response.json()
                if (!Array.isArray(data)) throw new Error('format')
                setMechanics(data)
                setMechanicsError(false)
            }).catch(error => { if (error.name !== 'AbortError') setMechanicsError(true) })
        return () => controller.abort()
    }, [token, order])
    const [form, setForm] = useState({
        plate: order?.plate || '',
        car_year: order?.car_year || '',
        client_name: order?.client_name || '',
        client_phone: order?.client_phone || '',
        number: order?.number || '',
        vin: order?.vin || '',
        engine_type: order?.engine_type || ''
    })

    useEffect(() => {
        fetch('/api/cars/brands', { headers: { Authorization: `Bearer ${token}` } })
            .then(r => r.json())
            .then(data => {
                if (!Array.isArray(data)) return
                setBrands(data)
                if (order && !selectedBrandId) {
                    const brand = data.find(item => item.name.toLowerCase() === order.car_brand?.toLowerCase())
                    if (brand) setSelectedBrandId(brand.id)
                }
            })
    }, [token])

    useEffect(() => {
        if (selectedBrandId)
            fetch(`/api/cars/models/${selectedBrandId}`, { headers: { Authorization: `Bearer ${token}` } })
                .then(r => r.json())
                .then(data => {
                    if (!Array.isArray(data)) return
                    setModels(data)
                    if (order && !selectedModelId) {
                        const model = data.find(item => item.name.toLowerCase() === order.car_model?.toLowerCase())
                        if (model) setSelectedModelId(model.id)
                    }
                })
    }, [selectedBrandId, token])

    const filteredBrands = brands.filter(b => b.name.toLowerCase().includes(brandSearch.toLowerCase()))
    const filteredModels = models.filter(m => m.name.toLowerCase().includes(modelSearch.toLowerCase()))

    const handleSelectBrand = (brand) => {
        setBrandSearch(brand.name)
        setSelectedBrandId(brand.id)
        setSelectedModelId(null)
        setModelSearch('')
        setShowBrands(false)
        setShowModels(false)
    }

    const handleSelectModel = (model) => {
        setModelSearch(model.name)
        setSelectedModelId(model.id)
        setShowModels(false)
    }

    const handleParse1C = (str) => {
        if (!str.trim()) { setParsed(null); return }
        const plateMatch = str.match(/№\s*([A-ZА-ЯЁa-zа-яё0-9]+)/i)
        if (plateMatch) setForm(p => ({ ...p, plate: plateMatch[1].toUpperCase() }))
        const vinMatch = str.match(/VIN\s+(\S+)/i)
        if (vinMatch) setForm(p => ({ ...p, vin: vinMatch[1].toUpperCase() }))
        const cleaned = str.replace(/№\s*[A-ZА-ЯЁa-zа-яё0-9]+/i, '').replace(/VIN\s+\S+/i, '').trim()
        const words = cleaned.split(/\s+/).filter(Boolean)
        let foundBrand = brands.find(b => words.some(w => b.name.toLowerCase() === w.toLowerCase()))
        if (!foundBrand) foundBrand = brands.find(b => words.some(w => b.name.toLowerCase().includes(w.toLowerCase()) || w.toLowerCase().includes(b.name.toLowerCase())))
        setParsed({ plate: plateMatch?.[1]?.toUpperCase() || null, vin: Boolean(vinMatch), brand: foundBrand?.name || null, model: null })
        if (!foundBrand) return
        setBrandSearch(foundBrand.name)
        setSelectedBrandId(foundBrand.id)
        setSelectedModelId(null)
        const brandWords = foundBrand.name.toLowerCase().split(/\s+/)
        const remainingWords = words.filter(w => !brandWords.includes(w.toLowerCase()))
        fetch(`/api/cars/models/${foundBrand.id}`, { headers: { Authorization: `Bearer ${token}` } })
            .then(r => r.json())
            .then(loadedModels => {
                setModels(loadedModels)
                let foundModel = null
                for (let i = 0; i <= remainingWords.length - 3; i++) {
                    const c = `${remainingWords[i]} ${remainingWords[i + 1]} ${remainingWords[i + 2]}`
                    foundModel = loadedModels.find(m => m.name.toLowerCase() === c.toLowerCase())
                    if (foundModel) break
                }
                if (!foundModel) {
                    for (let i = 0; i <= remainingWords.length - 2; i++) {
                        const c = `${remainingWords[i]} ${remainingWords[i + 1]}`
                        foundModel = loadedModels.find(m => m.name.toLowerCase() === c.toLowerCase())
                        if (foundModel) break
                    }
                }
                if (!foundModel) foundModel = loadedModels.find(m => remainingWords.some(w => m.name.toLowerCase() === w.toLowerCase()))
                if (!foundModel) foundModel = loadedModels.find(m => remainingWords.some(w => m.name.toLowerCase().includes(w.toLowerCase()) || w.toLowerCase().includes(m.name.toLowerCase())))
                if (foundModel) {
                    setModelSearch(foundModel.name)
                    setSelectedModelId(foundModel.id)
                    setParsed(prev => prev && { ...prev, model: foundModel.name })
                }
            })
    }

    const handleSave = async () => {
        if (savingRef.current) return
        if (!selectedBrandId || !selectedModelId || !form.plate || !form.car_year || !form.client_name || !form.client_phone || !form.engine_type) {
            setErrors({ car_brand: !selectedBrandId, car_model: !selectedModelId, plate: !form.plate, car_year: !form.car_year, client_name: !form.client_name, client_phone: !form.client_phone, engine_type: !form.engine_type })
            setApiError('Заполните обязательные поля и выберите автомобиль из справочника')
            return
        }
        setErrors({})
        setApiError('')
        savingRef.current = true
        setSaving(true)
        const body = { ...form, car_brand: brandSearch, car_model: modelSearch, car_year: parseInt(form.car_year), number: form.number || null, vin: form.vin || null }
        if (!order) body.mechanic_id = mechanicId ? Number(mechanicId) : null
        try {
            const response = await fetch(order ? `/api/orders/${order.id}` : '/api/orders', {
                method: order ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify(body)
            })
            const data = await response.json()
            if (!response.ok) {
                setApiError(data.message || 'Не удалось сохранить заявку')
                setErrors(data.fields || {})
                return
            }
            onSave(data)
            onClose()
        } catch {
            setApiError('Ошибка соединения с сервером')
        } finally {
            savingRef.current = false
            setSaving(false)
        }
    }

    const field = name => ({ className: 'om-input', 'data-error': Boolean(errors[name]) })
    const required = <b aria-hidden="true">*</b>

    return (
        <div className="om-overlay">
            <style>{orderModalStyles}</style>
            <div className="om-sheet" role="dialog" aria-modal="true" aria-labelledby="om-title">
                <div className="om-head">
                    <div>
                        <h2 id="om-title">{order ? 'Редактировать' : 'Новая диагностика'}</h2>
                        <p>{order ? 'Изменения сразу появятся в заявке' : 'Поля со звёздочкой обязательны'}</p>
                    </div>
                    <button type="button" className="om-close" onClick={onClose} aria-label="Закрыть"><i className="ti ti-x" aria-hidden="true" /></button>
                </div>

                <div className="om-body">
                    {apiError && <div className="om-error" role="alert"><i className="ti ti-alert-circle" aria-hidden="true" />{apiError}</div>}

                    {!order && (
                        <div className="om-1c">
                            <label htmlFor="om-1c-input"><i className="ti ti-bolt" aria-hidden="true" />Данные из 1С</label>
                            <input
                                id="om-1c-input"
                                placeholder="Changan UNI-K темно-серый № H503AA134 VIN LS4ASE2A2PA705261"
                                onChange={e => handleParse1C(e.target.value)}
                            />
                            {parsed && (parsed.brand || parsed.plate || parsed.vin) ? (
                                <div className="om-1c-found">
                                    {parsed.brand && <span><i className="ti ti-check" aria-hidden="true" />{parsed.brand}{parsed.model ? ` ${parsed.model}` : ''}</span>}
                                    {parsed.plate && <span><i className="ti ti-check" aria-hidden="true" />{parsed.plate}</span>}
                                    {parsed.vin && <span><i className="ti ti-check" aria-hidden="true" />VIN</span>}
                                </div>
                            ) : (
                                <p className="om-1c-hint">Вставьте строку из 1С — марка, модель, гос. номер и VIN заполнятся сами</p>
                            )}
                        </div>
                    )}

                    <section className="om-section">
                        <p className="om-section-title"><i className="ti ti-car" aria-hidden="true" />Автомобиль</p>
                        <div className="om-grid">
                            <div className="om-field">
                                <label className="om-label">Марка {required}</label>
                                <input value={brandSearch} onChange={e => { setBrandSearch(e.target.value); setSelectedBrandId(null); setSelectedModelId(null); setModelSearch(''); setShowBrands(true) }} onFocus={() => setShowBrands(true)} onBlur={() => setTimeout(() => setShowBrands(false), 150)} placeholder="Начните вводить марку..." {...field('car_brand')} />
                                {showBrands && filteredBrands.length > 0 && (
                                    <div className="om-menu">
                                        {filteredBrands.slice(0, 20).map(b => (
                                            <div key={b.id} className="om-option" data-selected={b.id === selectedBrandId} onMouseDown={e => e.preventDefault()} onClick={() => handleSelectBrand(b)}>{b.name}</div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="om-field">
                                <label className="om-label">Модель {required}</label>
                                <input value={modelSearch} onChange={e => { setModelSearch(e.target.value); setSelectedModelId(null); setShowModels(true) }} onFocus={() => { if (selectedBrandId) setShowModels(true) }} onBlur={() => setTimeout(() => setShowModels(false), 150)} placeholder={selectedBrandId ? 'Начните вводить модель...' : 'Сначала выберите марку'} disabled={!selectedBrandId} {...field('car_model')} />
                                {showModels && filteredModels.length > 0 && (
                                    <div className="om-menu">
                                        {filteredModels.slice(0, 20).map(m => (
                                            <div key={m.id} className="om-option" data-selected={m.id === selectedModelId} onMouseDown={e => e.preventDefault()} onClick={() => handleSelectModel(m)}>{m.name}</div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="om-field">
                                <label className="om-label">Год выпуска {required}</label>
                                <input type="number" inputMode="numeric" value={form.car_year} onChange={e => setForm(p => ({ ...p, car_year: e.target.value }))} placeholder="2020" {...field('car_year')} />
                            </div>
                            <div className="om-field">
                                <label className="om-label">Тип двигателя {required}</label>
                                <div role="button" tabIndex={0} aria-haspopup="listbox" aria-expanded={showEngineTypes} className="om-input om-select" data-error={Boolean(errors.engine_type)} data-empty={!form.engine_type} onClick={() => setShowEngineTypes(p => !p)} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowEngineTypes(p => !p) } }}>
                                    <span>{form.engine_type || 'Выберите тип'}</span>
                                    <i className="ti ti-chevron-down" aria-hidden="true" />
                                </div>
                                {showEngineTypes && (
                                    <div className="om-menu" role="listbox">
                                        {engineTypes.map(type => (
                                            <div key={type} role="option" aria-selected={form.engine_type === type} className="om-option" data-selected={form.engine_type === type} onClick={() => { setForm(p => ({ ...p, engine_type: type })); setShowEngineTypes(false) }}>
                                                {type}
                                                {form.engine_type === type && <i className="ti ti-check" aria-hidden="true" />}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="om-field">
                                <label className="om-label">Гос. номер {required}</label>
                                <input value={form.plate} onChange={e => setForm(p => ({ ...p, plate: e.target.value.toUpperCase() }))} placeholder="А123БВ77" {...field('plate')} className="om-input om-plate" />
                            </div>
                            <div className="om-field">
                                <label className="om-label">VIN</label>
                                <input value={form.vin} onChange={e => setForm(p => ({ ...p, vin: e.target.value.toUpperCase() }))} placeholder="XW7RDREVX0S005823" {...field('vin')} />
                            </div>
                        </div>
                    </section>

                    <section className="om-section">
                        <p className="om-section-title"><i className="ti ti-user" aria-hidden="true" />Клиент</p>
                        <div className="om-field">
                            <label className="om-label">Имя клиента {required}</label>
                            <input value={form.client_name} onChange={e => setForm(p => ({ ...p, client_name: e.target.value }))} placeholder="Иванов Иван" {...field('client_name')} />
                        </div>
                        <div className="om-grid">
                            <div className="om-field">
                                <label className="om-label">Телефон клиента {required}</label>
                                <input
                                    type="tel"
                                    value={form.client_phone}
                                    onChange={e => setForm(p => ({ ...p, client_phone: formatPhone(e.target.value) }))}
                                    placeholder="+7 (900) 000-00-00"
                                    {...field('client_phone')}
                                />
                            </div>
                            <div className="om-field">
                                <label className="om-label">№ заказ-наряда <span>(необязательно)</span></label>
                                <input value={form.number} onChange={e => setForm(p => ({ ...p, number: e.target.value }))} placeholder="Если есть" {...field('number')} />
                            </div>
                        </div>
                    </section>

                    {!order && (
                        <section className="om-section">
                            <p className="om-section-title"><i className="ti ti-tool" aria-hidden="true" />Назначение</p>
                            <div className="om-field">
                                <label htmlFor="create-order-mechanic" className="om-label">Механик (необязательно)</label>
                                <select id="create-order-mechanic" value={mechanicId} disabled={saving} onChange={event => setMechanicId(event.target.value)} className="om-input om-native">
                                    <option value="">Без назначения — открытая заявка</option>
                                    {mechanics.map(mechanic => <option key={mechanic.id} value={mechanic.id}>{mechanic.name || `${mechanic.last_name} ${mechanic.first_name}`}</option>)}
                                </select>
                                <p className="om-note" data-tone={mechanicId ? 'assigned' : undefined}>
                                    <i className={`ti ${mechanicId ? 'ti-player-play' : 'ti-users'}`} aria-hidden="true" />
                                    {mechanicId ? 'Сразу перейдёт в работу к выбранному механику. Время диагностики начнётся при создании.' : 'Любой активный механик вашего салона сможет взять заявку в работу.'}
                                </p>
                                {mechanicsError && <p className="om-note" data-tone="error"><i className="ti ti-alert-circle" aria-hidden="true" />Не удалось загрузить механиков. Можно создать без назначения или открыть форму заново.</p>}
                            </div>
                        </section>
                    )}
                </div>

                <div className="om-foot">
                    <button type="button" className="om-submit" onClick={handleSave} disabled={saving}>
                        {!saving && <i className={`ti ${order ? 'ti-device-floppy' : 'ti-plus'}`} aria-hidden="true" />}
                        {saving ? 'Сохранение...' : order ? 'Сохранить изменения' : 'Создать диагностику'}
                    </button>
                </div>
            </div>
        </div>
    )
}
